import type { FeatureContent } from "@/lib/features/content";

export const ES_FEATURES: Partial<Record<string, FeatureContent>> = {
  "auto-clip": {
    related: [
      "/use-cases/clippers",
      "/use-cases/podcast-clipping",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
      "/compare/crayo-vs-klap",
    ],
    title: "Auto Clip",
    answer:
      "Auto Clip toma un video largo que subes, como un podcast, un stream o un webinar, y usa IA para encontrar y cortar los mejores momentos en clips cortos con subtítulos. Está hecho para clippers, podcasters, streamers y agencias que convierten grabaciones largas en video corto a gran volumen.",
    description:
      "Sube un video largo y deja que la IA encuentre los mejores momentos y los corte en clips listos para publicar.",
    heroPrefix: "Convierte videos largos en",
    heroHighlight: "clips virales",
    heroSuffix: "automáticamente",
    heroImage: "/features/auto-clip/auto-clip-hero.png",
    buttonText: "Clipea un video ahora",
    buttonLink: "/create/auto-clip",
    seoTitle: "Cortar clips con IA: Auto Clipper",
    seoDescription:
      "Sube podcasts, streams o cualquier video largo y deja que la IA corte los mejores momentos en clips cortos virales en segundos.",
    faqs: [
      {
        question: "¿Cómo elige Auto Clip los momentos?",
        answer:
          "La IA analiza el video completo buscando ganchos, como historias, remates y segmentos de alta energía, y puntúa cada clip para que publiques primero los más fuertes.",
      },
      {
        question: "¿Qué tan largo puede ser mi video?",
        answer:
          "El contenido largo es justo el punto: podcasts, streams y webinars funcionan muy bien. Los videos más largos solo tardan un poco más en procesarse.",
      },
      {
        question: "¿Los clips vienen con subtítulos?",
        answer:
          "Sí. Cada clip recibe subtítulos precisos y con estilo de forma automática, y puedes ajustar el diseño antes de exportar.",
      },
      {
        question: "¿Puedo editar los clips antes de publicarlos?",
        answer:
          "Sí. Abre cualquier clip en el editor para recortar, reencuadrar o cambiar el estilo de los subtítulos antes de exportar.",
      },
    ],
  },
  "ai-captions": {
    related: [
      "/features/auto-clip",
      "/features/video-editor",
      "/features/clip-cutter",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-submagic",
      "/compare/crayo-vs-captions-ai",
    ],
    title: "AI Captions",
    answer:
      "AI Captions transcribe la voz de un clip y la convierte en subtítulos animados sincronizados palabra por palabra, con 32 plantillas de subtítulos y transcripción en 56 idiomas hablados. Es para clippers, podcasters y streamers que quieren cada short subtitulado para verse sin sonido, sin escribir una sola palabra.",
    description:
      "Subtítulos precisos y animados en cada clip. Elige una plantilla, resalta palabras clave y ajusta el estilo en el editor.",
    heroPrefix: "Agrega",
    heroHighlight: "subtítulos con IA",
    heroSuffix: "a cada clip automáticamente",
    heroImage: "/features/ai-captions/ai-captions-hero.png",
    buttonText: "Subtitula un clip ahora",
    buttonLink: "/create/auto-clip",
    seoTitle: "Subtítulos automáticos con IA para Shorts, Reels y TikTok",
    seoDescription:
      "Genera subtítulos animados para Shorts, Reels y TikTok con IA. 32 plantillas de subtítulos, 56 idiomas hablados, palabras clave resaltadas y control total del estilo.",
    faqs: [
      {
        question: "¿Dónde se agregan los subtítulos?",
        answer:
          "Auto Clip y el editor de video con IA subtitulan cada clip que producen. En el editor de línea de tiempo también puedes generar subtítulos con IA en cualquier video, o escribir subtítulos manuales línea por línea.",
      },
      {
        question: "¿Qué idiomas puede transcribir Crayo?",
        answer:
          "56 idiomas hablados, incluidos inglés, español, portugués, francés, alemán, hindi, árabe, japonés y coreano. Eliges el idioma hablado antes de iniciar el trabajo para que la transcripción use el modelo correcto.",
      },
      {
        question: "¿Puedo cambiar cómo se ven los subtítulos?",
        answer:
          "Sí. Elige una de las 32 plantillas y luego ajusta palabras por línea, posición, color del texto, contorno, sombra, animación y énfasis. El resaltado de palabras clave destaca las palabras importantes, y los cambios pueden aplicarse a una línea o a todas.",
      },
      {
        question: "¿Puedo corregir una palabra que la transcripción entendió mal?",
        answer:
          "Sí. Abre el clip en el editor, selecciona la línea de subtítulo y edita el texto. La sincronización y el estilo se mantienen.",
      },
    ],
  },
  "find-viral-moments": {
    related: [
      "/features/auto-clip",
      "/features/clip-cutter",
      "/features/ai-captions",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
    ],
    title: "Find Viral Moments",
    answer:
      "Find Viral Moments analiza un podcast, stream o entrevista de hasta 3 horas, puntúa los momentos con más probabilidad de funcionar y guarda cada uno como su propio clip. Es para clippers y equipos de podcast que quieren primero la lista corta y después la edición.",
    description:
      "Sube un video largo, cuéntanos qué contiene y recibe un conjunto de momentos ordenados por puntaje que puedes revisar, recortar y guardar.",
    heroPrefix: "Encuentra los",
    heroHighlight: "momentos virales",
    heroSuffix: "de cualquier video largo",
    heroImage: "/features/find-viral-moments/find-viral-moments-hero.png",
    buttonText: "Encontrar momentos ahora",
    buttonLink: "/create/find-moments",
    seoTitle: "Encuentra momentos virales en videos largos con IA",
    seoDescription:
      "Sube un podcast, stream o entrevista y deja que la IA encuentre y puntúe los momentos que vale la pena publicar. Revisa, recorta y guarda hasta 50 clips de un solo video.",
    faqs: [
      {
        question: "¿Qué controlo antes de que empiece la búsqueda?",
        answer:
          "Cuántos momentos buscar (de 5 a 50), la duración de cada momento (30, 45, 60 o 90 segundos), el idioma hablado, el tipo de contenido (charla, podcast, entrevista, acción o detección automática) y una nota de enfoque opcional, como solo las partes sobre precios.",
      },
      {
        question: "¿Puedo revisar los momentos antes de que se guarden?",
        answer:
          "Sí. Activa Revisar antes de guardar y los momentos se abren en Clip Cutter, donde conservas, saltas o recortas cada uno antes de que se convierta en clip. Si lo dejas desactivado, cada momento se guarda en tus Assets en cuanto termina la búsqueda.",
      },
      {
        question: "¿Qué tan largo puede ser el video fuente?",
        answer:
          "Desde 1 minuto hasta 3 horas. Las fuentes más largas simplemente tardan más en analizarse.",
      },
      {
        question: "¿Cuánto cuesta en créditos?",
        answer:
          "Un crédito de flujo por cada momento encontrado. Los momentos que no encontramos se reembolsan, y puedes ejecutar hasta tres búsquedas a la vez.",
      },
    ],
  },
  "ai-reframe": {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/features/video-editor",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
    ],
    title: "AI Reframe",
    answer:
      "AI Reframe convierte una grabación 16:9 en un clip 9:16 detectando rostros, siguiendo a quien está hablando y moviendo el encuadre con movimientos de cámara suaves y sostenidos en vez de un seguimiento tembloroso. Es para clippers, podcasters y streamers que necesitan clips verticales donde quien habla siempre esté en cuadro.",
    description:
      "La detección de rostros y el seguimiento del hablante mantienen centrada a la persona mientras el encuadre pasa de horizontal a vertical.",
    heroPrefix: "Reencuadra de",
    heroHighlight: "16:9 a 9:16",
    heroSuffix: "con seguimiento de rostro",
    heroImage: "/features/ai-reframe/ai-reframe-hero.png",
    buttonText: "Reencuadra un video ahora",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Reframe: de 16:9 a 9:16 con seguimiento de rostro",
    seoDescription:
      "Convierte podcasts y streams horizontales en clips verticales. La IA encuentra los rostros, sigue a quien habla y mantiene el encuadre estable entre movimientos.",
    faqs: [
      {
        question: "¿Cómo decide dónde recortar?",
        answer:
          "Un modelo de detección de rostros encuentra cada rostro en la toma y el movimiento de los labios indica quién habla. El encuadre se centra en el hablante activo y solo se desliza a una nueva posición cuando la persona sale de una zona muerta, así el cuadro no tiembla con cada movimiento de cabeza.",
      },
      {
        question: "¿Qué pasa con grabaciones de pantalla o sin rostros?",
        answer:
          "El planificador clasifica cada clip. Las grabaciones de pantalla, los clips sin un rostro que seguir y los clips donde el único rostro es una pequeña webcam en una esquina conservan el cuadro completo en vez de un recorte borroso.",
      },
      {
        question: "¿Qué diseños puedo elegir?",
        answer:
          "Pantalla completa pone al hablante en todo el cuadro vertical, Streamer agrega un fondo difuminado, Dividido apila un segundo video debajo y Letterbox conserva el cuadro completo sin recorte. La salida puede ser 9:16, 1:1 o 4:5.",
      },
      {
        question: "¿Puedo ajustar el encuadre a mano?",
        answer:
          "Sí. Abre el clip en el editor, ve a Efectos y luego a Reencuadre, y edita los keyframes de cámara, o fija la cámara en un hablante durante todo el clip.",
      },
    ],
  },
  "video-editor": {
    related: [
      "/features/auto-clip",
      "/features/ai-captions",
      "/features/ai-reframe",
      "/features/clip-cutter",
      "/compare/crayo-vs-capcut",
      "/compare/crayo-vs-veed",
    ],
    title: "Video Editor",
    answer:
      "El editor de video de Crayo es un editor de línea de tiempo en el navegador para clips cortos, con pistas de video, subtítulos, texto y audio, además de zooms, transiciones, LUTs, rampas de velocidad y keyframes de cámara. Está incluido en todos los planes y pensado para clippers que terminan el resultado de Auto Clip o editan sus propias subidas desde cero.",
    description:
      "Una línea de tiempo multipista con subtítulos, zooms, transiciones, LUTs, rampas de velocidad, cortes de apoyo y keyframes de cámara. Sin instalar nada y sin marca de agua en ningún plan.",
    heroPrefix: "El",
    heroHighlight: "editor de video con IA",
    heroSuffix: "hecho para clips cortos",
    heroImage: "/features/video-editor/video-editor-hero.png",
    buttonText: "Abrir el editor",
    buttonLink: "/projects",
    seoTitle: "Editor de clips con IA para video corto",
    seoDescription:
      "Edita clips cortos en tu navegador en una línea de tiempo multipista. Subtítulos con IA, zooms, transiciones, LUTs, rampas de velocidad y keyframes de cámara en todos los planes.",
    faqs: [
      {
        question: "¿Qué hay en la línea de tiempo?",
        answer:
          "Pistas separadas para video, subtítulos, texto, audio y efectos. Divide un clip en el cabezal de reproducción, recorta el inicio o el final, reordena y superpón cortes de apoyo sobre el clip principal mientras el audio de abajo baja automáticamente.",
      },
      {
        question: "¿Qué efectos están incluidos?",
        answer:
          "Zooms con tiempo y suavizado, transiciones como fundido, deslizamiento, látigo, golpe, glitch y destellos, corrección de color con LUTs, rampas de velocidad con keyframes y un panel de Cámara y reencuadre para mover el recorte a lo largo del tiempo.",
      },
      {
        question: "¿Cómo se manejan los subtítulos?",
        answer:
          "Genera subtítulos con IA en el clip o escríbelos a mano, luego elige entre 32 plantillas y define palabras por línea, posición, animación, énfasis y resaltado de palabras clave. Los cambios pueden aplicarse a una línea o a todas.",
      },
      {
        question: "¿El editor está en todos los planes?",
        answer:
          "Sí. Todos los planes de Crayo incluyen el editor completo. Los planes se diferencian en los minutos de video terminado que puedes exportar cada mes, y la vista previa de efectos necesita Chrome o Edge.",
      },
    ],
  },
};
